import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export interface AdminEvent {
  id: string;
  name: string;
  start_date: string | null;
  end_date: string | null;
}

interface AdminEventContextValue {
  events: AdminEvent[];
  loading: boolean;
  selectedEventId: string;
  selectedEvent: AdminEvent | null;
  setSelectedEventId: (id: string) => void;
  /** Upcoming or currently running events, soonest first */
  upcomingEvents: AdminEvent[];
  /** Past events, most recent first */
  pastEvents: AdminEvent[];
}

const STORAGE_KEY = 'admin_selected_event_id';

const AdminEventContext = createContext<AdminEventContextValue | undefined>(undefined);

function isPast(event: AdminEvent): boolean {
  const ref = event.end_date || event.start_date;
  if (!ref) return false;
  return new Date(ref).getTime() < Date.now() - 24 * 60 * 60 * 1000;
}

export function AdminEventProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventIdState] = useState<string>('');

  useEffect(() => {
    (supabase as any)
      .from('festivals')
      .select('id, name, start_date, end_date')
      .order('start_date', { ascending: false })
      .then(({ data }: { data: AdminEvent[] | null }) => {
        setEvents(data ?? []);
        setLoading(false);
      });
  }, []);

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const upcoming = events.filter(e => !isPast(e))
      .sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''));
    const past = events.filter(isPast)
      .sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? ''));
    return { upcomingEvents: upcoming, pastEvents: past };
  }, [events]);

  // Resolve initial selection once events are loaded: URL → localStorage → nearest upcoming → most recent
  useEffect(() => {
    if (!events.length || selectedEventId) return;
    const isValid = (id: string | null) => !!id && events.some(e => e.id === id);
    const urlId = searchParams.get('event');
    const storedId = localStorage.getItem(STORAGE_KEY);
    const resolved = isValid(urlId) ? urlId!
      : isValid(storedId) ? storedId!
      : (upcomingEvents[0]?.id ?? events[0].id);
    setSelectedEventIdState(resolved);
    localStorage.setItem(STORAGE_KEY, resolved);
  }, [events, upcomingEvents, selectedEventId, searchParams]);

  // Keep the URL in sync with the selection so admin links are shareable
  useEffect(() => {
    if (!selectedEventId) return;
    if (searchParams.get('event') !== selectedEventId) {
      setSearchParams(prev => { prev.set('event', selectedEventId); return prev; }, { replace: true });
    }
  }, [selectedEventId, searchParams, setSearchParams]);

  const setSelectedEventId = (id: string) => {
    setSelectedEventIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  };

  const selectedEvent = events.find(e => e.id === selectedEventId) ?? null;

  return (
    <AdminEventContext.Provider value={{
      events,
      loading,
      selectedEventId,
      selectedEvent,
      setSelectedEventId,
      upcomingEvents,
      pastEvents,
    }}>
      {children}
    </AdminEventContext.Provider>
  );
}

export function useAdminEvent() {
  const ctx = useContext(AdminEventContext);
  if (!ctx) throw new Error('useAdminEvent must be used within an AdminEventProvider');
  return ctx;
}
